import { useEffect, useMemo, useState } from 'react'
import { Eye, Pencil, Plus, Trash2, X } from 'lucide-react'
import { PageContainer, PageHeader } from '../../components/Layout'
import { Button, Card, CardContent, Input, Select, Badge, Modal, ConfirmModal } from '../../components/UI'
import { rentalTechService } from '../../services/api'

const tierOptions = [
  { value: 'standard', label: 'Standard' },
  { value: 'preferred', label: 'Preferente' },
  { value: 'enterprise', label: 'Enterprise' }
]

const defaultCustomerForm = {
  id: '',
  companyName: '',
  contactName: '',
  documentNumber: '',
  email: '',
  phone: '',
  country: 'Colombia',
  city: '',
  address: '',
  tier: 'standard',
  isActive: true
}

const toForm = (customer) => ({
  id: customer.id,
  companyName: customer.company_name || '',
  contactName: customer.contact_name || '',
  documentNumber: customer.document_number || '',
  email: customer.email || '',
  phone: customer.phone || '',
  country: customer.country || 'Colombia',
  city: customer.city || '',
  address: customer.address || '',
  tier: customer.tier || 'standard',
  isActive: customer.is_active ?? true
})

function CustomerForm({ form, onChange, onSubmit, saving, submitLabel, onClear, disabled = false, submitIcon = null }) {
  return (
    <div className={disabled ? 'opacity-60 pointer-events-none' : ''}>
      <div className="space-y-3">
        <Input placeholder="Empresa" value={form.companyName} onChange={(e) => onChange({ ...form, companyName: e.target.value })} />
        <Input placeholder="Contacto" value={form.contactName} onChange={(e) => onChange({ ...form, contactName: e.target.value })} />
        <div className="grid grid-cols-1 gap-3">
          <Input placeholder="Documento (NIT/RFC/RUC)" value={form.documentNumber} onChange={(e) => onChange({ ...form, documentNumber: e.target.value })} />
          <Select options={tierOptions} value={form.tier} onChange={(e) => onChange({ ...form, tier: e.target.value })} />
        </div>
        <Input placeholder="Email" value={form.email} onChange={(e) => onChange({ ...form, email: e.target.value })} />
        <Input placeholder="Telefono" value={form.phone} onChange={(e) => onChange({ ...form, phone: e.target.value })} />
        <div className="grid grid-cols-1 gap-3">
          <Input placeholder="Pais" value={form.country} onChange={(e) => onChange({ ...form, country: e.target.value })} />
          <Input placeholder="Ciudad" value={form.city} onChange={(e) => onChange({ ...form, city: e.target.value })} />
        </div>
        <Input placeholder="Direccion" value={form.address} onChange={(e) => onChange({ ...form, address: e.target.value })} />

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={!!form.isActive} onChange={(e) => onChange({ ...form, isActive: e.target.checked })} />
          Cliente activo
        </label>

        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Button onClick={onSubmit} loading={saving} leftIcon={submitIcon}>
            {submitLabel}
          </Button>
          {onClear ? (
            <Button variant="ghost" leftIcon={<X className="w-4 h-4" />} onClick={onClear}>
              Limpiar
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default function RentalTechCustomers() {
  const [customers, setCustomers] = useState([])
  const [search, setSearch] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState(null)
  const [showViewModal, setShowViewModal] = useState(false)

  const [createForm, setCreateForm] = useState(defaultCustomerForm)
  const [creating, setCreating] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)

  const [editForm, setEditForm] = useState(defaultCustomerForm)
  const [editingCustomerId, setEditingCustomerId] = useState(null)
  const [updating, setUpdating] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)

  const [deletingId, setDeletingId] = useState(null)
  const [customerToDelete, setCustomerToDelete] = useState(null)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const loadCustomers = async () => {
    const response = await rentalTechService.getCustomers()
    setCustomers(response?.data?.data || [])
  }

  useEffect(() => {
    loadCustomers()
  }, [])

  const filteredCustomers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return customers
    return customers.filter((c) =>
      [c.company_name, c.contact_name, c.document_number, c.email, c.city, c.country]
        .map((v) => String(v || '').toLowerCase())
        .some((v) => v.includes(q))
    )
  }, [customers, search])

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === selectedCustomerId) || null,
    [customers, selectedCustomerId]
  )

  const handleView = (customer) => {
    setSelectedCustomerId(customer.id)
    setShowViewModal(true)
    setError('')
  }

  const handleOpenCreate = () => {
    setCreateForm(defaultCustomerForm)
    setShowCreateModal(true)
    setError('')
  }

  const handleStartEdit = (customer) => {
    setSelectedCustomerId(customer.id)
    setEditingCustomerId(customer.id)
    setEditForm(toForm(customer))
    setShowEditModal(true)
    setError('')
  }

  const handleCancelEdit = () => {
    setEditingCustomerId(null)
    setEditForm(defaultCustomerForm)
    setShowEditModal(false)
    setError('')
  }

  const createCustomer = async () => {
    setCreating(true)
    setMessage('')
    setError('')
    try {
      const response = await rentalTechService.saveCustomer(createForm)
      const saved = response?.data?.data || null
      setCreateForm(defaultCustomerForm)
      await loadCustomers()
      if (saved?.id) setSelectedCustomerId(saved.id)
      setShowCreateModal(false)
      setMessage('Cliente creado correctamente.')
    } catch (err) {
      setError(err?.message || 'No fue posible crear el cliente.')
    } finally {
      setCreating(false)
    }
  }

  const updateCustomer = async () => {
    if (!editingCustomerId) return

    setUpdating(true)
    setMessage('')
    setError('')
    try {
      const response = await rentalTechService.saveCustomer(editForm)
      const saved = response?.data?.data || null
      await loadCustomers()
      if (saved?.id) {
        setSelectedCustomerId(saved.id)
        setEditingCustomerId(saved.id)
        setEditForm(toForm(saved))
      }
      setShowEditModal(false)
      setMessage('Cliente actualizado correctamente.')
    } catch (err) {
      setError(err?.message || 'No fue posible actualizar el cliente.')
    } finally {
      setUpdating(false)
    }
  }

  const requestDeleteCustomer = (customer) => {
    setCustomerToDelete(customer)
    setError('')
  }

  const deleteCustomer = async () => {
    if (!customerToDelete) return

    setDeletingId(customerToDelete.id)
    setMessage('')
    setError('')
    try {
      await rentalTechService.deleteCustomer(customerToDelete.id)
      if (selectedCustomerId === customerToDelete.id) setSelectedCustomerId(null)
      if (editingCustomerId === customerToDelete.id) handleCancelEdit()
      await loadCustomers()
      setCustomerToDelete(null)
      setMessage('Cliente eliminado correctamente.')
    } catch (err) {
      setError(err?.message || 'No fue posible eliminar el cliente.')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Consultar clientes"
          subtitle="Acciones de crear, editar y eliminar en popup desde el listado."
        />

        {message ? <div className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded px-3 py-2">{message}</div> : null}
        {error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div> : null}

        <div className="grid grid-cols-1 gap-6">
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">Consultar clientes</h3>
                  <p className="text-xs text-gray-500">Acciones en popup para crear, editar y eliminar.</p>
                </div>
                <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto md:items-center">
                  <div className="w-full md:w-80">
                    <Input placeholder="Buscar cliente..." value={search} onChange={(e) => setSearch(e.target.value)} />
                  </div>
                  <Button leftIcon={<Plus className="w-4 h-4" />} onClick={handleOpenCreate}>
                    Crear cliente
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="bg-gray-50 border-b">
                    <tr className="text-left">
                      <th className="px-4 py-3">Empresa</th>
                      <th className="px-4 py-3">Contacto</th>
                      <th className="px-4 py-3">Tier</th>
                      <th className="px-4 py-3">Ubicacion</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3">Funciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.map((c) => (
                      <tr key={c.id} className={`border-b ${selectedCustomerId === c.id ? 'bg-primary-50/50' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{c.company_name}</div>
                          <div className="text-xs text-gray-500">{c.document_number || 'Sin documento'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div>{c.contact_name || 'Sin contacto'}</div>
                          <div className="text-xs text-gray-500">{c.email || 'Sin email'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="info">{c.tier}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div>{c.city || 'Sin ciudad'}</div>
                          <div className="text-xs text-gray-500">{c.country || 'Sin pais'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={c.is_active ? 'success' : 'gray'}>
                            {c.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="secondary" leftIcon={<Eye className="w-3.5 h-3.5" />} onClick={() => handleView(c)}>
                              Ver
                            </Button>
                            <Button size="sm" variant="outline" leftIcon={<Pencil className="w-3.5 h-3.5" />} onClick={() => handleStartEdit(c)}>
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                              onClick={() => requestDeleteCustomer(c)}
                              disabled={deletingId === c.id}
                            >
                              {deletingId === c.id ? 'Eliminando...' : 'Eliminar'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!filteredCustomers.length ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                          {customers.length ? 'No hay resultados para la busqueda.' : 'Aun no hay clientes registrados.'}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

        </div>

        <Modal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          size="lg"
          title={
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary-600" />
              <span>Detalle del cliente</span>
            </div>
          }
        >
          {selectedCustomer ? (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-1">Empresa</p>
                <p className="font-medium text-gray-900">{selectedCustomer.company_name}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedCustomer.document_number || 'Sin documento'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-1">Contacto</p>
                <p>{selectedCustomer.contact_name || '-'}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedCustomer.email || 'Sin email'}</p>
                <p className="text-xs text-gray-500">{selectedCustomer.phone || 'Sin telefono'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-1">Ubicacion</p>
                <p>{selectedCustomer.city || 'Sin ciudad'}, {selectedCustomer.country || 'Sin pais'}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedCustomer.address || 'Sin direccion'}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="info">{selectedCustomer.tier}</Badge>
                <Badge variant={selectedCustomer.is_active ? 'success' : 'gray'}>
                  {selectedCustomer.is_active ? 'Activo' : 'Inactivo'}
                </Badge>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                  Cerrar
                </Button>
                <Button
                  variant="outline"
                  leftIcon={<Pencil className="w-4 h-4" />}
                  onClick={() => {
                    setShowViewModal(false)
                    handleStartEdit(selectedCustomer)
                  }}
                >
                  Editar
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">No hay cliente seleccionado.</div>
          )}
        </Modal>

        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          size="lg"
          title={
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary-600" />
              <span>Crear cliente</span>
            </div>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-gray-500">Formulario de creacion en popup.</p>
            <CustomerForm
              form={createForm}
              onChange={setCreateForm}
              onSubmit={createCustomer}
              saving={creating}
              submitLabel="Crear cliente"
              onClear={() => setCreateForm(defaultCustomerForm)}
              submitIcon={<Plus className="w-4 h-4" />}
            />
          </div>
        </Modal>

        <Modal
          isOpen={showEditModal}
          onClose={handleCancelEdit}
          size="lg"
          title={
            <div className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary-600" />
              <span>Editar cliente</span>
            </div>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              {editingCustomerId ? 'Formulario de edicion en popup para el cliente seleccionado.' : 'Selecciona un cliente para editar.'}
            </p>
            <CustomerForm
              form={editForm}
              onChange={setEditForm}
              onSubmit={updateCustomer}
              saving={updating}
              submitLabel="Guardar cambios"
              onClear={handleCancelEdit}
              disabled={!editingCustomerId}
              submitIcon={<Pencil className="w-4 h-4" />}
            />
          </div>
        </Modal>

        <ConfirmModal
          isOpen={!!customerToDelete}
          onClose={() => setCustomerToDelete(null)}
          onConfirm={deleteCustomer}
          title="Confirmar eliminacion"
          message={customerToDelete ? `Se eliminara el cliente \"${customerToDelete.company_name}\". Esta accion no se puede deshacer.` : ''}
          confirmText={deletingId ? 'Eliminando...' : 'Eliminar'}
          cancelText="Cancelar"
          variant="danger"
          loading={!!deletingId}
        />
      </div>
    </PageContainer>
  )
}
